import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Chip, List, ListItem, ListItemAvatar, ListItemText, Avatar,
  Divider, InputAdornment, IconButton, Paper, Grid, Tabs, Tab, Card, CardContent,
  CardHeader, FormControl, InputLabel, Select, MenuItem, Slider, LinearProgress,
  Badge, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Insights as InsightsIcon,
  Recommend as RecommendIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const apiUrl = '/api/v1/ai-fields';

function AiFieldsPage() {
  const [aiFields, setAiFields] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedAi, setSelectedAi] = useState(null);
  const [tab, setTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({
    leadStage: 'all',
    minScore: 0,
    maxScore: 100,
  });

  // Fetch AI fields
  useEffect(() => {
    loadAiFields();
    loadAnalytics();
  }, [filters]);

  const loadAiFields = async () => {
    try {
      setLoading(true);
      const tkn = localStorage.getItem('tkn');
      const headers = tkn ? { Authorization: `Bearer ${tkn}` } : {};

      const queryParams = new URLSearchParams({
        ...filters,
        search,
        sortBy: 'lastUpdated',
        sortOrder: 'desc',
      });

      const response = await fetch(`${apiUrl}?${queryParams}`, {
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setAiFields(data.aiFields || []);
      }
    } catch (error) {
      console.error('Error loading AI fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn ? { Authorization: `Bearer ${tkn}` } : {};

      const response = await fetch(`${apiUrl}/analytics`, {
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleAddAiField = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);
  const handleSaveAiField = () => {
    setAddDialogOpen(false);
    setTab(0);
    loadAiFields();
  };

  const getLeadStageColor = (stage) => {
    const colors = {
      cold: 'default',
      warm: 'warning',
      hot: 'error',
      qualified: 'info',
      converted: 'success',
    };
    return colors[stage] || 'default';
  };

  const getEngagementColor = (level) => {
    const colors = {
      low: 'error',
      medium: 'warning',
      high: 'info',
      very_high: 'success',
    };
    return colors[level] || 'default';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Grid container spacing={3} sx={{ height: '100%' }}>
        {/* Left Sidebar - AI Fields List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              height: '100%', display: 'flex', flexDirection: 'column', p: 2,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  AI Lead Intelligence
                </Typography>
                <IconButton onClick={() => setAnalyticsDialog(true)} color="primary" size="small">
                  <AnalyticsIcon />
                </IconButton>
              </Stack>

              <TextField
                fullWidth
                size="small"
                placeholder="Search AI insights..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="primary" />,
                }}
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAiField}
                  sx={{ flex: 1 }}
                  size="small"
                >
                  Add AI Field
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadAiFields}
                  size="small"
                >
                  Refresh
                </Button>
              </Stack>
            </Box>

            {/* Filters */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FilterIcon sx={{ mr: 1, fontSize: 16 }} />
                <Typography variant="body2">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Lead Stage</InputLabel>
                    <Select
                      value={filters.leadStage}
                      onChange={(e) => setFilters({ ...filters, leadStage: e.target.value })}
                      label="Lead Stage"
                    >
                      <MenuItem value="all">All Stages</MenuItem>
                      <MenuItem value="cold">Cold</MenuItem>
                      <MenuItem value="warm">Warm</MenuItem>
                      <MenuItem value="hot">Hot</MenuItem>
                      <MenuItem value="qualified">Qualified</MenuItem>
                      <MenuItem value="converted">Converted</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Lead Score:
                      {' '}
                      {filters.minScore}
                      {' '}
                      -
                      {' '}
                      {filters.maxScore}
                    </Typography>
                    <Slider
                      value={[filters.minScore, filters.maxScore]}
                      onChange={(_, value) => setFilters({
                        ...filters,
                        minScore: value[0],
                        maxScore: value[1],
                      })}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                      size="small"
                    />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* AI Fields List */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <LinearProgress sx={{ width: '100%' }} />
                </Box>
              ) : aiFields.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No AI fields found.</Typography>
                </Box>
              ) : (
                <List>
                  {aiFields.map((ai) => (
                    <ListItem
                      key={ai._id}
                      button
                      selected={selectedAi && selectedAi._id === ai._id}
                      onClick={() => setSelectedAi(ai)}
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                        py: 1.5,
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={ai.aiConfidence}
                          color={getScoreColor(ai.aiConfidence)}
                          max={100}
                        >
                          <Avatar sx={{ width: 40, height: 40 }}>
                            <AutoAwesomeIcon />
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={(
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {ai.contact}
                            </Typography>
                            <Chip
                              label={ai.leadScore}
                              color={getScoreColor(ai.leadScore)}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={ai.leadStage}
                              color={getLeadStageColor(ai.leadStage)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Stack>
                        )}
                        secondary={(
                          <Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {ai.aiIntent}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Chip
                                label={`${ai.dealProbability}%`}
                                size="small"
                                color={getScoreColor(ai.dealProbability)}
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                              <Chip
                                label={ai.engagementLevel}
                                size="small"
                                color={getEngagementColor(ai.engagementLevel)}
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                            </Stack>
                          </Box>
                        )}
                      />
                      <IconButton edge="end" size="small">
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Details */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: '100%', p: 2, display: 'flex', flexDirection: 'column',
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Overview" />
              <Tab label="Preferences" />
              <Tab label="Behavior" />
              <Tab label="Analytics" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
              {tab === 0 && <AiFieldOverview aiField={selectedAi} />}
              {tab === 1 && <AiFieldPreferences aiField={selectedAi} />}
              {tab === 2 && <AiFieldBehavior aiField={selectedAi} />}
              {tab === 3 && <AiFieldAnalytics aiField={selectedAi} />}
              {tab === 4 && <AiFieldForm aiField={selectedAi} onSave={() => setTab(0)} mode="edit" />}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 0 } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.main', color: 'primary.contrastText', py: 2, px: 3,
        }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>Add New AI Field</Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <AiFieldForm onSave={handleSaveAiField} onCancel={handleCloseAddDialog} mode="add" />
        </Box>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialog} onClose={() => setAnalyticsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>AI Analytics Dashboard</DialogTitle>
        <DialogContent>
          {analytics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Lead Stage Distribution" />
                  <CardContent>
                    {analytics.leadStageStats?.map((stat) => (
                      <Box key={stat._id} sx={{ mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">{stat._id}</Typography>
                          <Typography variant="body2" fontWeight={600}>{stat.count}</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={(stat.count / analytics.leadStageStats.reduce((sum, s) => sum + s.count, 0)) * 100}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Top Tags" />
                  <CardContent>
                    <Stack direction="row" flexWrap="wrap" spacing={1}>
                      {analytics.topTags?.map((tag) => (
                        <Chip
                          key={tag._id}
                          label={`${tag._id} (${tag.count})`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Component for AI Field Overview Tab
function AiFieldOverview({ aiField }) {
  if (!aiField) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
      }}
      >
        <Typography variant="h6" color="text.secondary">
          Select an AI field to view details.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="AI Intent" avatar={<PsychologyIcon />} />
          <CardContent>
            <Typography variant="body1">{aiField.aiIntent}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="AI Summary" avatar={<InsightsIcon />} />
          <CardContent>
            <Typography variant="body1">{aiField.aiSummary}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader title="Next Best Action" avatar={<TimelineIcon />} />
          <CardContent>
            <Typography variant="h6" color="primary">
              {aiField.nextBestAction || 'Contact for property viewing'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Component for AI Field Preferences Tab
function AiFieldPreferences({ aiField }) {
  if (!aiField) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
      }}
      >
        <Typography variant="h6" color="text.secondary">
          Select an AI field to view preferences.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Property Preferences" avatar={<LocationIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">
                  Preferred Zip:
                  {aiField.preferredZip}
                </Typography>
                <Typography variant="subtitle2">
                  Neighborhoods:
                  {aiField.preferredNeighborhoods?.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  Property Types:
                  {aiField.preferredPropertyTypes?.join(', ')}
                </Typography>
                <Typography variant="subtitle2">
                  Bedrooms:
                  {aiField.bedroomPreference}
                </Typography>
                <Typography variant="subtitle2">
                  Bathrooms:
                  {aiField.bathroomPreference}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Budget & Timeline" avatar={<MoneyIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">
                  Budget Range: $
                  {aiField.budgetRange?.min?.toLocaleString()}
                  {' '}
                  - $
                  {aiField.budgetRange?.max?.toLocaleString()}
                </Typography>
                <Typography variant="subtitle2">
                  Preferred: $
                  {aiField.budgetRange?.preferred?.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  Buying Timeline:
                  {aiField.buyingTimeline}
                </Typography>
                <Typography variant="subtitle2">
                  Selling Timeline:
                  {aiField.sellingTimeline}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Component for AI Field Behavior Tab
function AiFieldBehavior({ aiField }) {
  if (!aiField) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
      }}
      >
        <Typography variant="h6" color="text.secondary">
          Select an AI field to view behavior.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Communication & Engagement" avatar={<PersonIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">
                  Preferred Channel:
                  {aiField.communicationPreference}
                </Typography>
                <Typography variant="subtitle2">
                  Engagement Level:
                  {aiField.engagementLevel}
                </Typography>
                <Typography variant="subtitle2">
                  Response Time:
                  {aiField.responseTime}
                  {' '}
                  hours
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  Activity Frequency:
                  {aiField.activityFrequency}
                </Typography>
                <Typography variant="subtitle2">
                  Last Activity:
                  {new Date(aiField.lastActivity).toLocaleDateString()}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Risk Assessment" avatar={<WarningIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">
                  Credit Risk:
                  {aiField.creditRisk}
                </Typography>
                <Typography variant="subtitle2">
                  Financing Likelihood:
                  {aiField.financingLikelihood}
                </Typography>
                <Typography variant="subtitle2">
                  Urgency Level:
                  {aiField.urgencyLevel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Risk Factors:</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {aiField.riskFactors?.map((factor, idx) => (
                    <Chip key={idx} label={factor} size="small" color="error" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Component for AI Field Analytics Tab
function AiFieldAnalytics({ aiField }) {
  if (!aiField) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
      }}
      >
        <Typography variant="h6" color="text.secondary">
          Select an AI field to view analytics.
        </Typography>
      </Box>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Performance Metrics" avatar={<AssessmentIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">Lead Score</Typography>
                <LinearProgress
                  variant="determinate"
                  value={aiField.leadScore}
                  color={getScoreColor(aiField.leadScore)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {aiField.leadScore}
                  /100
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Deal Probability</Typography>
                <LinearProgress
                  variant="determinate"
                  value={aiField.dealProbability}
                  color={getScoreColor(aiField.dealProbability)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {aiField.dealProbability}
                  %
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">AI Confidence</Typography>
                <LinearProgress
                  variant="determinate"
                  value={aiField.aiConfidence}
                  color={getScoreColor(aiField.aiConfidence)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {aiField.aiConfidence}
                  %
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Market Intelligence" avatar={<TrendingUpIcon />} />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">
                  Market Sentiment:
                  {aiField.marketSentiment}
                </Typography>
                <Typography variant="subtitle2">
                  Price Sensitivity:
                  {aiField.priceSensitivity}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  Estimated Deal Value: $
                  {aiField.estimatedDealValue?.toLocaleString()}
                </Typography>
                <Typography variant="subtitle2">
                  Data Quality:
                  {aiField.dataQuality}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Component for AI Field Form (Add/Edit)
function AiFieldForm({
  aiField, onSave, onCancel, mode,
}) {
  const [form, setForm] = useState({
    contact: aiField?.contact || '',
    leadScore: aiField?.leadScore || 50,
    leadStage: aiField?.leadStage || 'cold',
    aiIntent: aiField?.aiIntent || '',
    buyingTimeline: aiField?.buyingTimeline || 'unknown',
    preferredZip: aiField?.preferredZip || '',
    aiSummary: aiField?.aiSummary || '',
    aiTags: aiField?.aiTags?.join(', ') || '',
    dealProbability: aiField?.dealProbability || 50,
    engagementLevel: aiField?.engagementLevel || 'medium',
    communicationPreference: aiField?.communicationPreference || 'email',
  });

  const handleSave = async () => {
    try {
      const tkn = localStorage.getItem('tkn');
      const headers = tkn ? { Authorization: `Bearer ${tkn}` } : {};

      const formData = {
        ...form,
        aiTags: form.aiTags ? form.aiTags.split(',').map((t) => t.trim()) : [],
      };

      const url = mode === 'edit' ? `${apiUrl}/${aiField._id}` : apiUrl;
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving AI field:', error);
    }
  };

  return (
    <Stack spacing={3}>
      <TextField
        label="Contact Name"
        value={form.contact}
        onChange={(e) => setForm({ ...form, contact: e.target.value })}
        fullWidth
        required
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Lead Score"
            type="number"
            value={form.leadScore}
            onChange={(e) => setForm({ ...form, leadScore: parseInt(e.target.value) })}
            fullWidth
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Lead Stage</InputLabel>
            <Select
              value={form.leadStage}
              onChange={(e) => setForm({ ...form, leadStage: e.target.value })}
              label="Lead Stage"
            >
              <MenuItem value="cold">Cold</MenuItem>
              <MenuItem value="warm">Warm</MenuItem>
              <MenuItem value="hot">Hot</MenuItem>
              <MenuItem value="qualified">Qualified</MenuItem>
              <MenuItem value="converted">Converted</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TextField
        label="AI Intent"
        value={form.aiIntent}
        onChange={(e) => setForm({ ...form, aiIntent: e.target.value })}
        fullWidth
        multiline
        rows={2}
      />

      <TextField
        label="AI Summary"
        value={form.aiSummary}
        onChange={(e) => setForm({ ...form, aiSummary: e.target.value })}
        fullWidth
        multiline
        rows={3}
      />

      <TextField
        label="AI Tags (comma separated)"
        value={form.aiTags}
        onChange={(e) => setForm({ ...form, aiTags: e.target.value })}
        fullWidth
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Deal Probability (%)"
            type="number"
            value={form.dealProbability}
            onChange={(e) => setForm({ ...form, dealProbability: parseInt(e.target.value) })}
            fullWidth
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Preferred Zip Code"
            value={form.preferredZip}
            onChange={(e) => setForm({ ...form, preferredZip: e.target.value })}
            fullWidth
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Engagement Level</InputLabel>
            <Select
              value={form.engagementLevel}
              onChange={(e) => setForm({ ...form, engagementLevel: e.target.value })}
              label="Engagement Level"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="very_high">Very High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Communication Preference</InputLabel>
            <Select
              value={form.communicationPreference}
              onChange={(e) => setForm({ ...form, communicationPreference: e.target.value })}
              label="Communication Preference"
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="in_person">In Person</MenuItem>
              <MenuItem value="social_media">Social Media</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          {mode === 'edit' ? 'Update' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default AiFieldsPage;
